import Paper from "@material-ui/core/Paper";
import Slide from "@material-ui/core/Slide";
import { makeStyles } from "@material-ui/core/styles";
import { RefObject, useEffect, useState } from "react";
import { colors } from "../../colors";
import {
  ToolbarDateNavigation,
  ToolbarDateNavigationProps,
} from "../../components/UI/Toolbar/ToolbarDateNavigation";

export const compactNavigationHeight = 48;
const desktopHeaderHeight = 64;

export function useIsElementScrolledOutOfView(
  elementRef: RefObject<HTMLElement>,
  enabled: boolean
) {
  const [isOutOfView, setIsOutOfView] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element || typeof IntersectionObserver === "undefined") {
      setIsOutOfView(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsOutOfView(!entry.isIntersecting),
      { rootMargin: `-${desktopHeaderHeight}px 0px 0px 0px` }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [elementRef, enabled]);

  return isOutOfView;
}

export interface PerformanceProCompactNavigationProps
  extends Omit<ToolbarDateNavigationProps, "compact"> {
  isMobileSize: boolean;
  visible: boolean;
}

export function PerformanceProCompactNavigation({
  isMobileSize,
  visible,
  ...navigationProps
}: PerformanceProCompactNavigationProps) {
  const classes = useStyles();

  const navigation = (
    <ToolbarDateNavigation {...navigationProps} compact={true} />
  );

  if (isMobileSize) {
    return (
      <Paper
        square
        elevation={2}
        className={classes.mobileRoot}
        data-testid="compact-navigation"
      >
        {navigation}
      </Paper>
    );
  }

  return (
    <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
      <Paper
        square
        elevation={4}
        className={classes.desktopRoot}
        data-testid="compact-navigation"
      >
        {navigation}
      </Paper>
    </Slide>
  );
}

const useStyles = makeStyles((theme) => ({
  mobileRoot: {
    position: "sticky",
    top: theme.spacing(8),
    [theme.breakpoints.down("sm")]: {
      top: theme.spacing(6),
    },
    zIndex: theme.zIndex.appBar,
    height: compactNavigationHeight,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    backgroundColor: colors.grey93,
  },
  desktopRoot: {
    position: "fixed",
    top: desktopHeaderHeight,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    height: compactNavigationHeight,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    backgroundColor: colors.grey93,
  },
}));
